import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../../shared/providers/ref';
import { IHandle } from '../../../../../shared/types/i-handle';
import { ArcGisQueryRepositoryProvider } from '../../../../../shared/providers/repository-providers';
import { PopupInteractionState } from './popup-interaction-state';
import { MapProvider } from '../../map/map-provider';
import { LayerProvider } from '../../../shared/layer/layer-provider';
import { FeatureEntity, LayerEntity, LayerTypeEnum } from '@gishub/domain';
import MapView from '@arcgis/core/views/MapView';
import Graphic from '@arcgis/core/Graphic';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import Layer from '@arcgis/core/layers/Layer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import type WMSSublayer from '@arcgis/core/layers/support/WMSSublayer';
import { Configuration } from '../../../../../shared/configuration/configuration';

@Injectable()
export class PopupInteractionNotifier extends BaseNotifier<PopupInteractionState> {
  private static readonly INITIAL: PopupInteractionState = {
    enabled: false,
    loading: false,
  };
  private static readonly QUERYABLE_TYPES = new Set<string>(['feature', 'map-image', 'wms']);

  private clickHandle: IHandle | null = null;

  constructor(ref: Ref, private readonly config: Configuration) {
    super(ref, PopupInteractionNotifier.INITIAL);

    this.trackDisposable(() => {
      this.clickHandle?.remove();
      this.clickHandle = null;
    });

    this.listen(() => {
      const view = this.ref.watch(MapProvider).state().view;
      this.applyToView(view as MapView);
    });
  }

  setEnabled(enabled: boolean): void {
    const view = this.ref.watch(MapProvider).state().view;
    if (!enabled && view) view.closePopup();
    this.setState({ enabled, loading: false });
    this.applyCursor('');
    if (view) this.applyToView(view as MapView);
  }

  private applyToView(view: MapView | null): void {
    if (this.clickHandle) {
      this.clickHandle.remove();
      this.clickHandle = null;
    }
    if (!view) return;

    const enabled = this.snapshot().enabled;
    view.popupEnabled = !enabled;
    if (!enabled) return;

    this.clickHandle = view.on('click', (event) => this.onMapClick(view, event));
  }

  private applyCursor(cursor: string): void {
    const view = this.ref.watch(MapProvider).state().view;
    if (view?.container) (view.container as HTMLElement).style.cursor = cursor;
  }

  private applyLoading(loading: boolean): void {
    this.updateState((s) => ({ ...s, loading }));
    this.applyCursor(loading ? 'wait' : '');
  }

  private async onMapClick(view: MapView, event: any): Promise<void> {
    const mapPoint = event.mapPoint;
    if (!mapPoint) return;

    this.applyCursor('pointer');
    this.applyLoading(true);
    try {
      const { graphics: nativeHits, layerIds: nativeLayerIds, hasAggregate } = await this.collectNativeHits(view, event);

      // Clic sobre una burbuja de agrupamiento: la resuelve `MapNotifier` volando al recuadro
      // de los puntos agrupados, así que aquí no se abre popup ni se consultan los servicios
      // por el punto (devolverían features sueltas de la zona y taparían el vuelo).
      if (hasAggregate) {
        view.closePopup();
        return;
      }

      const manualByLayer = await this.collectManualHits(view, mapPoint);
      const features = [...nativeHits];
      for (const [layerId, graphics] of manualByLayer) {
        if (!nativeLayerIds.has(layerId)) features.push(...graphics);
      }

      if (features.length === 0) {
        view.closePopup();
        return;
      }

      view.openPopup({ features, location: mapPoint }).catch((err) => {
        console.error('[Identify] Error abriendo popup:', err);
      });
    } catch (error) {
      console.error('[PopupInteractionNotifier] Error al identificar:', error);
    } finally {
      this.applyLoading(false);
    }
  }

  private async collectNativeHits(view: MapView, event: any): Promise<{ graphics: Graphic[]; layerIds: Set<string>; hasAggregate: boolean }> {
    const response = await view.hitTest(event);
    const layerIds = new Set<string>();
    const graphics: Graphic[] = [];
    let hasAggregate = false;
    for (const result of response.results as any[]) {
      const graphic = result.graphic as Graphic | undefined;
      // Un gráfico agregado representa un conjunto de puntos (burbuja de agrupamiento), no una
      // feature: no alimenta el popup; se informa para que el clic se resuelva acercando.
      if (graphic?.isAggregate) {
        hasAggregate = true;
        continue;
      }
      if (!graphic || Object.keys(graphic.attributes ?? {}).length === 0) continue;
      if (result.layer?.id) layerIds.add(result.layer.id);
      graphics.push(this.ensurePopupTemplate(graphic, result.layer?.title));
    }
    return { graphics, layerIds, hasAggregate };
  }

  private ensurePopupTemplate(graphic: Graphic, layerTitle?: string): Graphic {
    if (!graphic.popupTemplate) {
      const fields = this.collectFields([{ attributes: graphic.attributes }]);
      graphic.popupTemplate = this.buildPopupTemplate(layerTitle || 'Identificación', fields);
    }
    return graphic;
  }

  private async collectManualHits(view: MapView, mapPoint: any): Promise<Map<string, Graphic[]>> {
    const map = this.ref.watch(MapProvider).state().map ?? view.map;
    const result = new Map<string, Graphic[]>();
    if (!map) return result;

    const entities = this.entitiesById();
    const layers = this.flattenOperationalLayers(map.layers);
    const queried: string[] = [];

    for (const layer of layers) {
      if (!layer.visible || !PopupInteractionNotifier.QUERYABLE_TYPES.has(layer.type)) continue;

      if (layer.type === 'wms') {
        const url = this.layerUrl(layer);
        if (!url) continue;
        try {
          const features = await this.queryWMS(view, layer, mapPoint);
          queried.push(`${layer.title} (${layer.type})`);
          if (features.length === 0) continue;

          const entity = entities.get(layer.id);
          const title = layer.title || entity?.name || 'Identificación';
          const queryFields = entity?.queryFields;
          result.set(layer.id, this.buildGraphics(title, queryFields, features, mapPoint));
        } catch (error) {
          console.error(`[Identify] Error consultando '${layer.title}' (${layer.type}, ${url}):`, error);
        }
        continue;
      }

      if (layer.type === 'map-image') {
        await (layer as MapImageLayer).when().catch(() => {});
      }

      for (const endpoint of await this.serviceEndpoints(layer, entities.get(layer.id))) {
        try {
          const features = await this.queryByPoint(endpoint.url, mapPoint, endpoint.type);
          queried.push(`${endpoint.title} (${layer.type})`);
          if (features.length === 0) continue;

          const queryFields = entities.get(layer.id)?.queryFields;
          result.set(endpoint.key, this.buildGraphics(endpoint.title, queryFields, features, mapPoint));
        } catch (error) {
          console.error(
            `[Identify] Error consultando '${endpoint.title}' (${layer.type}, ${endpoint.url}):`,
            error,
          );
        }
      }
    }

    if (result.size === 0) {
      const visibleQueryable = layers.filter(
        (layer) =>
          layer.visible &&
          PopupInteractionNotifier.QUERYABLE_TYPES.has(layer.type) &&
          Boolean(this.layerUrl(layer)),
      );
      console.warn(
        `[Identify] Sin resultados de consulta. Consultadas: ${queried.length ? queried.join(' | ') : 'ninguna'}. Consultables visibles: ${visibleQueryable.length}.`,
      );
    }
    return result;
  }

  private flattenOperationalLayers(layers: Iterable<Layer>): Layer[] {
    const result: Layer[] = [];
    for (const layer of layers) {
      if (layer.type === 'group') {
        result.push(...this.flattenOperationalLayers((layer as GroupLayer).layers));
      } else {
        result.push(layer);
      }
    }
    return result;
  }

  private entitiesById(): Map<string, LayerEntity> {
    const byId = new Map<string, LayerEntity>();
    const visit = (nodes: LayerEntity[]): void => {
      for (const node of nodes) {
        byId.set(node.id, node);
        if (node.layers) visit(node.layers);
      }
    };
    visit(this.ref.watch(LayerProvider).state().layers);
    return byId;
  }

  private layerUrl(layer: Layer): string | null {
    return (layer as unknown as { url?: string | null }).url ?? null;
  }

  private proxyUrl(url: string, entity: LayerEntity | undefined): string {
    return entity?.hasProxy ? `${this.config.proxyUrl}${url}` : url;
  }

  private async serviceEndpoints(
    layer: Layer,
    entity: LayerEntity | undefined,
  ): Promise<{ key: string; url: string; title: string; type: LayerTypeEnum }[]> {
    const original = entity?.url || this.layerUrl(layer);
    if (!original) return [];

    const match = original.match(/^(https?:\/\/.*?\/(?:MapServer|FeatureServer))(?:\/(\d+))?/i);
    if (!match) return [];

    const serviceBase = match[1];
    const urlLayerId = match[2];

    if (layer.type === 'map-image') {
      const sublayers = (layer as MapImageLayer).sublayers ?? [];
      const visibleIds = [...sublayers.filter((s) => s.visible)].map((s) => s.id);

      let sublayerIds: number[] = visibleIds.length
        ? visibleIds
        : urlLayerId
          ? [Number(urlLayerId)]
          : (entity?.layers ?? [])
              .map((child) => Number(child.referenceSource))
              .filter((id) => !Number.isNaN(id));

      return sublayerIds.map((id) => {
        const endpoint = `${serviceBase}/${id}`;
        const sub = sublayers.find((s) => s.id === id);
        return {
          key: `${layer.id}:${id}`,
          url: this.proxyUrl(endpoint, entity),
          title: sub?.title || entity?.name || layer.title || 'Identificación',
          type: LayerTypeEnum.MapServer,
        };
      });
    }

    return [
      {
        key: layer.id,
        url: this.proxyUrl(original, entity),
        title: entity?.name || layer.title || 'Identificación',
        type: LayerTypeEnum.FeatureServer,
      },
    ];
  }

  private async queryByPoint(url: string, mapPoint: any, type: LayerTypeEnum): Promise<FeatureEntity[]> {
    const queryRepository = this.ref.watch(ArcGisQueryRepositoryProvider).instance;
    const entity = new LayerEntity({
      id: url,
      name: url,
      url,
      type,
    });
    const result = await queryRepository.queryByPoint(entity, {
      x: mapPoint.longitude,
      y: mapPoint.latitude,
      spatialReference: { wkid: 4326 },
    });
    return result.success ? (result.data ?? []) : [];
  }

  private async queryWMS(view: MapView, layer: Layer, mapPoint: any): Promise<FeatureEntity[]> {
    const wmsLayer = layer as WMSLayer;
    await wmsLayer.when();
    const url = wmsLayer.url;
    if (!url) return [];

    const screen = view.toScreen(mapPoint);
    const extent = view.extent;
    if (!screen || screen.x == null || screen.y == null || !extent) return [];

    let layerNames = (wmsLayer.sublayers ?? [])
      .map((sublayer: WMSSublayer) => sublayer.name)
      .filter((name): name is string => Boolean(name))
      .join(',');
    if (!layerNames && wmsLayer.title) layerNames = wmsLayer.title;
    if (!layerNames) return [];

    const wkid = extent.spatialReference?.wkid ?? 4326;
    const version = wmsLayer.version ?? '1.3.0';
    const axisSwapped = version.startsWith('1.3') && wkid === 4326;
    const bbox = axisSwapped
      ? `${extent.ymin},${extent.xmin},${extent.ymax},${extent.xmax}`
      : `${extent.xmin},${extent.ymin},${extent.xmax},${extent.ymax}`;

    const queryRepository = this.ref.watch(ArcGisQueryRepositoryProvider).instance;
    const result = await queryRepository.queryWMSFeatureInfo(url, {
      layers: layerNames,
      bbox,
      width: Math.max(1, Math.round(view.width)),
      height: Math.max(1, Math.round(view.height)),
      x: Math.round(screen.x),
      y: Math.round(screen.y),
      version,
      spatialReference: { wkid },
    });
    return result.success ? (result.data ?? []) : [];
  }

  private buildGraphics(title: string, queryFields: string[] | undefined, features: FeatureEntity[], mapPoint: any): Graphic[] {
    const fields = queryFields?.length ? queryFields : this.collectFields(features);
    const template = this.buildPopupTemplate(title, fields);
    return features.map(
      (feature) => new Graphic({ attributes: feature.attributes, geometry: mapPoint, popupTemplate: template }),
    );
  }

  private buildPopupTemplate(title: string, fields: string[]): PopupTemplate {
    return new PopupTemplate({
      title,
      content: [{ type: 'fields', fieldInfos: fields.map((field) => ({ fieldName: field, label: field })) }],
    });
  }

  private collectFields(features: FeatureEntity[]): string[] {
    const fields = new Set<string>();
    for (const feature of features) {
      for (const field of Object.keys(feature.attributes ?? {})) fields.add(field);
    }
    return [...fields];
  }
}
